import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Heart, Baby, Sparkles } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { IMAGES } from "@/data/content";

const HIGHLIGHTS = [
  { icon: Heart, label: "15+ anni di pasta di zucchero" },
  { icon: Baby, label: "Insegna ai bambini dai 4 ai 10 anni" },
  { icon: Sparkles, label: "Il metodo della semplicità" },
];

const STORY_REST = [
  "Ma la passione era troppo grande. Ogni compleanno o ricorrenza diventava l'occasione perfetta per mettermi alla prova, finché quello che era nato come un hobby si è trasformato nel mio lavoro. Negli anni ho realizzato torte decorate a due e tre piani per eventi speciali, collaborato con pasticcerie e creato allestimenti scenografici.",
  "Poi, 10 anni fa, è arrivata la svolta che mi ha dato la gioia più grande: insegnare la modellazione ai bambini dai 4 ai 10 anni. Lavorare con i più piccoli mi ha insegnato la lezione più preziosa di tutte: la bellezza della semplicità. Ho sviluppato un metodo basato su passaggi chiari, visivi e consequenziali, capace di guidare chiunque a creare personaggi e decorazioni meravigliose, partendo da forme base semplicissime.",
  "Ed è esattamente questo che troverai qui: un percorso guidato passo dopo passo, studiato per darti tutti gli strumenti, i trucchi sui materiali e le tecniche pratiche. Che tu sia alle prime armi o desideri affinare la tua manualità, riuscirai a trasformare un semplice biscotto tondo o una torta in un'opera d'arte unica.",
];

const ChiSono = () => {
  const [open, setOpen] = useState(false);

  return (
    <section data-testid="chi-sono" className="mb-16">
      <Reveal>
        <div className="grid items-start gap-8 overflow-hidden rounded-[2rem] border border-pink-100 bg-white p-6 shadow-[0_20px_60px_rgba(45,36,36,0.08)] sm:p-10 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="relative">
            <div className="absolute -inset-4 rounded-[2rem] bg-blush/50 blur-xl" />
            <img
              src={IMAGES.giorgia}
              alt="Giorgia Perletta nella sua cucina con una torta decorata con personaggi in pasta di zucchero"
              data-testid="chi-sono-photo"
              className="relative aspect-[4/3] w-full rotate-1 rounded-3xl border-4 border-white object-cover shadow-[0_20px_50px_rgba(45,36,36,0.15)]"
            />
            <p className="absolute -bottom-4 right-3 rounded-xl bg-white/95 px-4 py-2 font-hand text-xl text-rosa-dark shadow-lg backdrop-blur">
              Giorgia Perletta
            </p>
          </div>
          <div>
            <Eyebrow>Chi Sono</Eyebrow>
            <h2 className="mt-2 font-display text-2xl font-bold tracking-tight text-cacao sm:text-3xl">
              Ciao e benvenuta!
            </h2>
            <p className="mt-4 text-sm text-cacao-muted leading-relaxed sm:text-base">
              Sono Giorgia Perletta. La mia avventura con la pasta di zucchero
              è iniziata oltre 15 anni fa, mossa dalla voglia di fare qualcosa
              di speciale per il terzo compleanno di mio figlio. All'epoca non
              era affatto semplice: i tutorial online quasi non esistevano,
              trovare gli strumenti giusti era una vera impresa e ogni
              passaggio è stato frutto di tentativi, errori ed esperimenti.
            </p>
            <AnimatePresence initial={false}>
              {open && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                  data-testid="chi-sono-story-full"
                >
                  {STORY_REST.map((p, i) => (
                    <p key={i} className="mt-4 text-sm text-cacao-muted leading-relaxed sm:text-base">
                      {p}
                    </p>
                  ))}
                  <p className="mt-5 font-hand text-2xl text-rosa-dark">
                    Mettiamoci all'opera e diamo forma alla tua creatività!
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
            <button
              data-testid="chi-sono-toggle"
              onClick={() => setOpen((o) => !o)}
              className="group mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-rosa-dark transition-colors hover:text-rosa"
            >
              {open ? "Chiudi la storia" : "Leggi tutta la storia"}
              <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
            </button>
            <div className="mt-6 flex flex-wrap gap-2">
              {HIGHLIGHTS.map(({ icon: Icon, label }, i) => (
                <span
                  key={i}
                  data-testid={`chi-sono-highlight-${i + 1}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-burro px-3.5 py-1.5 text-xs font-semibold text-cacao"
                >
                  <Icon className="h-3.5 w-3.5 text-oro" />
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
};

export default ChiSono;
